import { atom } from "jotai";
import { meApi, type Affiliation } from "../api/client";

// ── Affiliations (archive) store ──
//
// Affiliations = the galaxies the user has LEFT (departed memberships). This is
// the GalaxySwitcher's "Archive" section AND the surface any departure mutation
// must move: the moment a member is marked departed (the member_removed webhook
// or the observation-driven residency reconcile), the archive must appear WITHOUT
// a reload.
//
// Why a shared atom instead of GalaxySwitcher's old local state: the switcher
// loaded affiliations once, behind a `loaded` guard, into component state. A
// departure performed out of band (the webhook / reconcile) had no way to
// invalidate that local cache, so the
// archive only surfaced on full reload. "Reload-to-refresh" is the failure mode
// the cache-freshness policy forbids. Lifting the list into an atom with an
// explicit refresh action gives every mutation site one place to invalidate.
//
// The atom is the single source of computed truth (frontend-manifest #1);
// mutations are functions (refreshAffiliationsAtom), never setter reuse (#2).

export const affiliationsAtom = atom<Affiliation[]>([]);

// Has the list been fetched at least once this session? Distinguishes "no
// archives" from "not loaded yet" so the switcher can lazy-load on first need
// without re-requesting on every open.
export const affiliationsLoadedAtom = atom<boolean>(false);

// Tracks the latest fetch so a refresh triggered while one is in flight does NOT
// return the older (possibly pre-mutation) promise. Each call chains a FRESH
// fetch after the current one, so the final write always reflects the newest
// server state — the depart/restore that triggered the refresh is never raced
// out by an in-flight switcher-open load. A null tail means "idle".
let tail: Promise<void> | null = null;

// refreshAffiliationsAtom (re)fetches the archive list and is the ONLY way to
// mutate affiliationsAtom. Call it after any depart/restore, and lazily on first
// switcher open. Errors are swallowed (an unreachable archive simply hides the
// section) but still mark loaded so the lazy effect doesn't hammer.
export const refreshAffiliationsAtom = atom(null, (_get, set) => {
  const run = () =>
    meApi
      .affiliations()
      .then((a) => {
        set(affiliationsAtom, a || []);
      })
      .catch((e) => {
        console.error("affiliations refresh failed", e);
      })
      .finally(() => {
        set(affiliationsLoadedAtom, true);
      });
  // Chain after any in-flight fetch so the newest call wins the last write.
  const next = tail ? tail.then(run, run) : run();
  tail = next.finally(() => {
    if (tail === next) tail = null;
  });
  return next;
});
